import {
  clampLabelLengthMm,
  type LabelDimensionsMm,
  normalizeRollWidthMm,
} from './label-geometry.constants';

// --- Predefined label format catalog ---------------------------------------
// The per-tenant label format is persisted as the (tape, length) pair in
// `Tenant.printLabelRollWidthMm` / `printLabelLengthMm`; the pair is the
// natural key of this catalog, so every entry MUST have a unique
// (tapeMm, lengthMm) combination (enforced by tests).
// ponytail: if two formats ever need the same pair with different layouts,
// switch to persisting the format id instead.

export const LABEL_FORMAT_CATEGORIES = ['logistics', 'production', 'internal'] as const;
export type LabelFormatCategory = (typeof LABEL_FORMAT_CATEGORIES)[number];

export type LabelLayoutKind = 'full' | 'qr-only';
export type LabelMediaKind = 'die-cut' | 'continuous';

export interface LabelFormat {
  /** Stable key used by UI and tests, e.g. '102x152', 'continuous-62'. */
  id: string;
  categories: readonly LabelFormatCategory[];
  /** Brother media code, e.g. 'DK-11241'. Shown raw in the UI when present. */
  dkCode?: string;
  kind: LabelMediaKind;
  layout: LabelLayoutKind;
  /** Tape width = heightMm of the printed landscape sticker. */
  tapeMm: number;
  /** Print length along the feed = widthMm. Fixed for continuous rolls. */
  lengthMm: number;
}

export const LABEL_FORMAT_CATALOG: readonly LabelFormat[] = [
  // Trasporto e logistica
  {
    id: '102x152',
    categories: ['logistics'],
    dkCode: 'DK-11241',
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 102,
    lengthMm: 152,
  },
  {
    id: '103x164',
    categories: ['logistics'],
    dkCode: 'DK-11247',
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 103,
    lengthMm: 164,
  },
  {
    id: '100x150',
    categories: ['logistics'],
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 100,
    lengthMm: 150,
  },
  {
    id: '105x148',
    categories: ['logistics'],
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 105,
    lengthMm: 148,
  },
  {
    id: '62x100',
    categories: ['logistics', 'production'],
    dkCode: 'DK-11202',
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 62,
    lengthMm: 100,
  },
  // Produzione
  {
    id: '62x29',
    categories: ['production'],
    dkCode: 'DK-11209',
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 62,
    lengthMm: 29,
  },
  {
    id: '62x86',
    categories: ['production'],
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 62,
    lengthMm: 86,
  },
  // 62x72 matches the Prisma defaults, so never-configured tenants resolve here.
  {
    id: 'continuous-62',
    categories: ['production'],
    kind: 'continuous',
    layout: 'full',
    tapeMm: 62,
    lengthMm: 72,
  },
  // Uso interno e laboratori
  {
    id: '38x90',
    categories: ['internal'],
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 38,
    lengthMm: 90,
  },
  {
    id: '29x90',
    categories: ['internal'],
    dkCode: 'DK-11201',
    kind: 'die-cut',
    layout: 'full',
    tapeMm: 29,
    lengthMm: 90,
  },
  {
    id: '24x24',
    categories: ['internal'],
    dkCode: 'DK-11218',
    kind: 'die-cut',
    layout: 'qr-only',
    tapeMm: 24,
    lengthMm: 24,
  },
  // Continuous 38/29 print at 100mm so the pair doesn't collide with the
  // 38x90 / 29x90 die-cut entries.
  {
    id: 'continuous-38',
    categories: ['internal'],
    kind: 'continuous',
    layout: 'full',
    tapeMm: 38,
    lengthMm: 100,
  },
  {
    id: 'continuous-29',
    categories: ['internal'],
    kind: 'continuous',
    layout: 'full',
    tapeMm: 29,
    lengthMm: 100,
  },
];

export const DEFAULT_LABEL_FORMAT_ID = 'continuous-62';

export function getLabelFormatById(id: string): LabelFormat | undefined {
  return LABEL_FORMAT_CATALOG.find((format) => format.id === id);
}

export function findLabelFormat(tapeMm: number, lengthMm: number): LabelFormat | undefined {
  return LABEL_FORMAT_CATALOG.find(
    (format) => format.tapeMm === tapeMm && format.lengthMm === lengthMm,
  );
}

// Tall tapes keep 1mm of breathing room per side (matches the historical 62mm
// behavior: content band 60mm, 1mm offset); tapes <= 60mm use the full height.
function dimensionsFor(widthMm: number, heightMm: number): LabelDimensionsMm {
  const usableHeightMm = heightMm <= 60 ? heightMm : heightMm - 2;
  return {
    widthMm,
    heightMm,
    usableHeightMm,
    verticalOffsetMm: (heightMm - usableHeightMm) / 2,
  };
}

export interface ResolvedLabelFormat {
  /** null = legacy pair persisted before the catalog existed (pass-through). */
  format: LabelFormat | null;
  dimensions: LabelDimensionsMm;
  layout: LabelLayoutKind;
}

/**
 * Single resolution point from the persisted (rollWidth, length) pair to a
 * renderer-ready format. Exact catalog match wins; any other pair is a legacy
 * value (60|62 x 72-100 by historical validation) and passes through the old
 * clamp/snap path so existing tenants keep printing exactly what they had.
 */
export function resolveLabelFormat(input: {
  rollWidthMm: number;
  lengthMm: number;
}): ResolvedLabelFormat {
  const format = findLabelFormat(input.rollWidthMm, input.lengthMm);
  if (format) {
    // Content is always composed in landscape (long side horizontal): on
    // formats like 62x29 the tape side is the wide one and the printer driver
    // rotates the page onto the roll.
    return {
      format,
      dimensions: dimensionsFor(
        Math.max(format.tapeMm, format.lengthMm),
        Math.min(format.tapeMm, format.lengthMm),
      ),
      layout: format.layout,
    };
  }
  return {
    format: null,
    dimensions: dimensionsFor(
      clampLabelLengthMm(input.lengthMm),
      normalizeRollWidthMm(input.rollWidthMm),
    ),
    layout: 'full',
  };
}

/** Resolves a stored {rollWidth, length} config into renderer-ready dimensions. */
export function resolveLabelDimensions(input: {
  rollWidthMm: number;
  lengthMm: number;
}): LabelDimensionsMm {
  return resolveLabelFormat(input).dimensions;
}

export const DEFAULT_LABEL_DIMENSIONS: LabelDimensionsMm = resolveLabelDimensions({
  rollWidthMm: 62,
  lengthMm: 72,
});
